import {
	GetProductDocument,
	ProductImageCropUnit,
	useConfirmProductImageUploadMutation,
	useGenerateProductImageUploadUrlMutation,
	useGetProductQuery,
	useUpdateProductImageCropMutation,
	useUpdateProductMutation,
} from '@codezo/graphql';
import { ArrowLeft, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CreateBrandStep } from '../../components/brands/CreateBrandStep';
import { BrandSelector } from '../../components/selectors/BrandSelector';
import { AnimatedHeight } from '../../components/ui/animated-height';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogTitle,
} from '../../components/ui/dialog';
import { ImageCropper, type ImageCropCoords } from '../../components/ui/image-cropper';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { NumberInput } from '../../components/ui/number-input';
import { Tabs } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { SpecificationsEditor } from '../../components/products/specifications/SpecificationsEditor';
import {
	countSpecEntries,
	emptySpecForm,
	formToSpecInput,
	specToForm,
	type SpecFormState,
} from '@codezo/product-specs';
import { StagedImage, stageFiles, uploadProductImage } from '../../lib/product-image-upload';
import { cn } from '../../lib/utils';
import { ExistingImage, ProductExistingImages } from './ProductExistingImages';
import { ProductImageStager } from './ProductImageStager';

export type EditProductTab = 'details' | 'specifications' | 'assets';

interface EditProductModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	productId: string;
	onSaved: () => void;
	/** Which tab the modal opens on — lets a card deep-link to its own section. */
	initialTab?: EditProductTab;
}

type SavePhase = 'idle' | 'saving' | 'uploading';

/** What the inline crop editor is currently editing. */
type CropTarget = { kind: 'existing'; image: ExistingImage } | { kind: 'staged'; index: number };

export function EditProductModal({
	open,
	onOpenChange,
	productId,
	onSaved,
	initialTab = 'details',
}: EditProductModalProps) {
	const [tab, setTab] = useState<string>(initialTab);
	const [name, setName] = useState('');
	const [sku, setSku] = useState('');
	const [description, setDescription] = useState('');
	const [brandId, setBrandId] = useState('');
	const [brandLabel, setBrandLabel] = useState('');
	/* Inline "create brand" step — replaces the form, like the crop view. */
	const [brandStep, setBrandStep] = useState<{ seed: string } | null>(null);
	const [dimLength, setDimLength] = useState('');
	const [dimWidth, setDimWidth] = useState('');
	const [dimHeight, setDimHeight] = useState('');
	const [dimUnit, setDimUnit] = useState('cm');
	const [weight, setWeight] = useState('');
	const [specs, setSpecs] = useState<SpecFormState>(emptySpecForm());
	const [newImages, setNewImages] = useState<StagedImage[]>([]);
	const [phase, setPhase] = useState<SavePhase>('idle');
	const [uploadProgress, setUploadProgress] = useState(0);
	const [cropTarget, setCropTarget] = useState<CropTarget | null>(null);

	const { data: productData } = useGetProductQuery({
		variables: { id: productId },
		skip: !productId || !open,
		fetchPolicy: 'no-cache',
	});

	const refetchQueries = [{ query: GetProductDocument, variables: { id: productId } }];

	const [updateProduct] = useUpdateProductMutation({ refetchQueries });
	const [generateUploadUrl] = useGenerateProductImageUploadUrlMutation();
	const [confirmUpload] = useConfirmProductImageUploadMutation({ refetchQueries });
	const [updateCrop, { loading: savingCrop }] = useUpdateProductImageCropMutation();

	const product = productData?.getProduct;
	const existingImages = product?.images ?? [];
	const busy = phase !== 'idle';

	/* ── Crop (inline view-swap, not a nested modal) ── */
	/* Existing images persist immediately; staged images store the crop locally
	 * and upload it with the file on save. */

	const cropSource =
		cropTarget?.kind === 'existing'
			? {
					src: cropTarget.image.url || '',
					crop: cropTarget.image.crop,
					key: cropTarget.image.id,
				}
			: cropTarget?.kind === 'staged'
				? {
						src: newImages[cropTarget.index]?.preview || '',
						crop: newImages[cropTarget.index]?.crop,
						key: `staged-${cropTarget.index}`,
					}
				: null;

	const handleSaveCrop = async (coords: ImageCropCoords) => {
		if (!cropTarget) return;

		if (cropTarget.kind === 'staged') {
			const { index } = cropTarget;
			setNewImages((prev) =>
				prev.map((img, i) => (i === index ? { ...img, crop: coords } : img))
			);
			setCropTarget(null);
			return;
		}

		try {
			await updateCrop({
				variables: {
					productId,
					imageId: cropTarget.image.id,
					crop: {
						x: coords.x,
						y: coords.y,
						width: coords.width,
						height: coords.height,
						zoom: coords.zoom,
						rotation: coords.rotation,
						aspectRatio: coords.aspectRatio,
						unit: ProductImageCropUnit.Percent,
					},
				},
				refetchQueries,
			});
			toast.success('Crop updated');
			onSaved();
			setCropTarget(null);
		} catch {
			toast.error('Failed to update crop');
		}
	};

	/* ── Populate form on load ── */

	useEffect(() => {
		if (product && open) {
			setName(product.name || '');
			setSku(product.sku || '');
			setDescription(product.description || '');
			setBrandId(product.brandId || '');
			setBrandLabel(product.brand?.name || '');
			setDimLength(product.dimensions?.length ? String(product.dimensions.length) : '');
			setDimWidth(product.dimensions?.width ? String(product.dimensions.width) : '');
			setDimHeight(product.dimensions?.height ? String(product.dimensions.height) : '');
			setDimUnit(product.dimensions?.unit || 'cm');
			setWeight(product.weight ? String(product.weight) : '');
			setSpecs(specToForm(product.specifications));
		}
	}, [product, open]);

	/* Reopening from a different card should land on that card's tab. */
	useEffect(() => {
		if (open) setTab(initialTab);
	}, [open, initialTab]);

	/* ── Staging ── */

	const addFiles = (files: File[]) => {
		setNewImages((prev) => [...prev, ...stageFiles(files)]);
	};

	const removeImage = (index: number) => {
		setNewImages((prev) => {
			const next = [...prev];
			if (next[index].preview) URL.revokeObjectURL(next[index].preview);
			next.splice(index, 1);
			return next;
		});
	};

	const finishClose = () => {
		newImages.forEach((img) => img.preview && URL.revokeObjectURL(img.preview));
		setNewImages([]);
		setTab(initialTab);
		setPhase('idle');
		setUploadProgress(0);
		setCropTarget(null);
		onOpenChange(false);
	};

	const handleOpenChange = (next: boolean) => {
		if (busy || savingCrop) return; // don't allow closing mid-save
		if (!next) finishClose();
		else onOpenChange(true);
	};

	/* ── Save: update details, then upload any staged images ── */

	const handleSave = async () => {
		if (!name.trim() || busy) return;

		setPhase('saving');
		try {
			const { data } = await updateProduct({
				variables: {
					input: {
						id: productId,
						name: name.trim(),
						sku: sku.trim() || undefined,
						description: description.trim() || undefined,
						/* Null clears it — undefined would leave the old brand in place. */
						brandId: brandId || null,
						weight: weight ? parseFloat(weight) : undefined,
						dimensions: {
							length: dimLength ? parseFloat(dimLength) : 0,
							width: dimWidth ? parseFloat(dimWidth) : 0,
							height: dimHeight ? parseFloat(dimHeight) : 0,
							unit: dimUnit,
						},
						/* Replaces the stored specification wholesale. */
						specifications: formToSpecInput(specs),
					},
				},
			});

			if (!data?.updateProduct.success) {
				toast.error(data?.updateProduct.message || 'Failed to update');
				setPhase('idle');
				return;
			}

			/* No staged images → done. */
			if (newImages.length === 0) {
				toast.success('Product updated');
				onSaved();
				finishClose();
				return;
			}

			/* Upload staged images, dropping each from the queue as it succeeds. */
			setPhase('uploading');
			setUploadProgress(0);
			const pending = [...newImages];
			const total = pending.length;
			let failedAt = -1;

			for (let i = 0; i < total; i++) {
				try {
					await uploadProductImage({
						productId,
						image: pending[i],
						isPrimary: existingImages.length === 0 && i === 0,
						generateUploadUrl,
						confirmUpload,
						onProgress: (fraction) => setUploadProgress(((i + fraction) / total) * 100),
					});
				} catch (err) {
					failedAt = i;
					toast.error(
						err instanceof Error
							? `Some images failed to upload: ${err.message}`
							: 'Some images failed to upload'
					);
					break;
				}
			}

			if (failedAt === -1) {
				toast.success('Product updated');
				onSaved();
				finishClose();
				return;
			}

			/* Keep the failed + not-yet-attempted files staged so they can be retried. */
			pending
				.slice(0, failedAt)
				.forEach((img) => img.preview && URL.revokeObjectURL(img.preview));
			setNewImages(pending.slice(failedAt));
			onSaved();
			setPhase('idle');
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update product');
			setPhase('idle');
		}
	};

	const saveLabel =
		phase === 'saving' ? 'Saving…' : phase === 'uploading' ? 'Uploading…' : 'Save changes';

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-lg" showCloseButton={false}>
				<AnimatedHeight step={cropSource ? 'crop' : brandStep ? 'brand' : tab}>
					{brandStep ? (
						/* Create-brand view (replaces the edit form — no modal-on-modal) */
						<CreateBrandStep
							initialName={brandStep.seed}
							onCancel={() => setBrandStep(null)}
							onCreated={(brand) => {
								if (brand) {
									setBrandId(brand.id);
									setBrandLabel(brand.name);
								}
								setBrandStep(null);
							}}
						/>
					) : cropSource ? (
						<>
							{/* Crop view (replaces the edit form — no modal-on-modal) */}
							<div className="flex shrink-0 items-center gap-2 px-5 pt-5 pb-3">
								<button
									onClick={() => !savingCrop && setCropTarget(null)}
									disabled={savingCrop}
									className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1 disabled:cursor-default disabled:opacity-50"
								>
									<ArrowLeft size={15} />
								</button>
								<DialogTitle className="text-lg font-medium text-text-1">
									Crop image
								</DialogTitle>
							</div>
							<ImageCropper
								key={cropSource.key}
								imageSrc={cropSource.src}
								aspect={1}
								hideRotate
								restrictPosition
								saveLabel="Save crop"
								saving={cropTarget?.kind === 'existing' && savingCrop}
								initialCrop={toInitialCrop(cropSource.crop)}
								onCancel={() => setCropTarget(null)}
								onSaveCrop={handleSaveCrop}
							/>
						</>
					) : (
						<>
							{/* Header + Tabs */}
							<div className="shrink-0">
								<div className="flex items-start justify-between px-5 pt-5 pb-3">
									<div>
										<DialogTitle className="text-lg font-medium text-text-1">
											Edit product
										</DialogTitle>
										<p className="mt-1 text-sm text-text-3">
											Update product details and manage images.
										</p>
									</div>
									<button
										onClick={() => handleOpenChange(false)}
										disabled={busy}
										className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1 disabled:cursor-default disabled:opacity-50"
									>
										<X size={15} />
									</button>
								</div>
								<div className="px-5">
									<Tabs
										value={tab}
										onChange={setTab}
										items={[
											{ value: 'details', label: 'Details' },
											{
												value: 'specifications',
												label: 'Specifications',
												count: countSpecEntries(specs) || undefined,
											},
											{
												value: 'assets',
												label: 'Assets',
												count: existingImages.length + newImages.length,
											},
										]}
									/>
								</div>
							</div>

							{/* Body */}
							<DialogBody className="scroll-thin max-h-[65vh] p-5">
								<div className="grid grid-cols-1 [&>*]:col-start-1 [&>*]:row-start-1 [&>*]:min-w-0">
									{/* Details tab */}
									<div
										className={
											tab !== 'details'
												? 'pointer-events-none invisible'
												: undefined
										}
									>
										<div className="flex flex-col gap-5">
											<div className="flex flex-col gap-1.5">
												<Label>
													Name <span className="text-destructive">*</span>
												</Label>
												<Input
													value={name}
													onChange={(e) => setName(e.target.value)}
													placeholder="Product name"
													maxLength={75}
												/>
											</div>

											<div className="flex flex-col gap-1.5">
												<Label>SKU</Label>
												<Input
													value={sku}
													onChange={(e) => setSku(e.target.value)}
													placeholder="Optional"
													maxLength={30}
												/>
											</div>

											<div className="flex flex-col gap-1.5">
												<Label>Description</Label>
												<Textarea
													value={description}
													onChange={(e) => setDescription(e.target.value)}
													placeholder="Optional description"
													maxLength={500}
													rows={3}
												/>
											</div>

											<div className="flex flex-col gap-1.5">
												<Label>Brand</Label>
												<BrandSelector
													value={brandId}
													onChange={(next, label) => {
														setBrandId(next);
														setBrandLabel(label ?? '');
													}}
													selectedLabel={brandLabel}
													clearable
													placeholder="Select a brand (optional)"
													skip={!open}
													onCreateNew={(query) =>
														setBrandStep({ seed: query })
													}
												/>
											</div>

											{/* Dimensions */}
											<div>
												<Label className="mb-2">Dimensions</Label>
												<div className="grid grid-cols-3 gap-2">
													{(
														[
															['Length', dimLength, setDimLength],
															['Width', dimWidth, setDimWidth],
															['Height', dimHeight, setDimHeight],
														] as const
													).map(([label, value, setter]) => (
														<div
															key={label}
															className="flex flex-col gap-1"
														>
															<label className="text-xs text-text-3">
																{label}
															</label>
															<NumberInput
																value={value}
																onChange={(e) =>
																	setter(
																		(
																			e.target as HTMLInputElement
																		).value
																	)
																}
																placeholder="0"
																min={0}
																step={0.01}
															/>
														</div>
													))}
												</div>
											</div>

											{/* Unit */}
											<div className="flex flex-col gap-1.5">
												<Label>Unit</Label>
												<div className="flex gap-1.5">
													{(['cm', 'mm', 'in', 'm'] as const).map((u) => (
														<button
															key={u}
															type="button"
															onClick={() => setDimUnit(u)}
															className={cn(
																'flex h-8 cursor-pointer items-center justify-center rounded-lg border px-4 text-xs font-medium transition-colors',
																dimUnit === u
																	? 'border-accent bg-accent-soft text-accent'
																	: 'border-line-2 bg-transparent text-text-2 hover:border-line-3'
															)}
														>
															{u}
														</button>
													))}
												</div>
											</div>

											{/* Weight */}
											<div className="flex flex-col gap-1.5">
												<Label>Weight (kg)</Label>
												<NumberInput
													value={weight}
													onChange={(e) =>
														setWeight(
															(e.target as HTMLInputElement).value
														)
													}
													placeholder="0"
													min={0}
													step={0.01}
													className="max-w-[200px]"
												/>
											</div>
										</div>
									</div>

									{/* Specifications tab */}
									<div
										className={
											tab !== 'specifications'
												? 'pointer-events-none invisible'
												: undefined
										}
									>
										<SpecificationsEditor
											value={specs}
											onChange={setSpecs}
											disabled={busy}
										/>
									</div>

									{/* Assets tab */}
									<div
										className={
											tab !== 'assets'
												? 'pointer-events-none invisible'
												: undefined
										}
									>
										<div className="flex flex-col gap-4">
											<ProductImageStager
												images={newImages}
												onAddFiles={addFiles}
												onRemove={removeImage}
												onCropImage={(index) =>
													setCropTarget({ kind: 'staged', index })
												}
												disabled={busy}
											/>

											{phase === 'uploading' && (
												<div className="rounded-[var(--r-3)] border border-line-1 bg-surface-fill/60 px-3 py-3">
													<div className="mb-1.5 h-1 overflow-hidden rounded-full bg-surface-input">
														<div
															className="h-full rounded-full bg-accent transition-all duration-300"
															style={{ width: `${uploadProgress}%` }}
														/>
													</div>
													<div className="text-center text-xs text-text-4">
														Uploading images…
													</div>
												</div>
											)}

											<ProductExistingImages
												productId={productId}
												images={existingImages}
												onChanged={onSaved}
												onCropImage={(image) =>
													setCropTarget({ kind: 'existing', image })
												}
												disabled={busy}
											/>

											{existingImages.length === 0 &&
												newImages.length === 0 && (
													<p className="text-center text-sm text-text-4">
														Staged images upload when you save.
													</p>
												)}
										</div>
									</div>
								</div>
							</DialogBody>

							<DialogFooter>
								<Button
									variant="outline"
									onClick={() => handleOpenChange(false)}
									disabled={busy}
								>
									Cancel
								</Button>
								<Button onClick={handleSave} disabled={!name.trim() || busy}>
									{saveLabel}
								</Button>
							</DialogFooter>
						</>
					)}
				</AnimatedHeight>
			</DialogContent>
		</Dialog>
	);
}

/** Map a stored display crop into the cropper's editable coordinates. */
function toInitialCrop(crop?: ExistingImage['crop']): ImageCropCoords | null {
	if (!crop) return null;
	return {
		x: crop.x,
		y: crop.y,
		width: crop.width,
		height: crop.height,
		zoom: crop.zoom ?? 1,
		rotation: crop.rotation ?? 0,
		aspectRatio: crop.aspectRatio ?? '1:1',
	};
}
